import { AppPage } from "models/AppPage";
import { getStaggerDelay } from "utils/navigationStyles";

import { DropdownItem } from "./DropdownItem";

interface DropdownGridLayoutProps {
    children: AppPage[];
    currentTag?: string;
    onSettingsClick?: (e: React.MouseEvent) => void;
}

/**
 * Dynamic grid layout for "콘텐츠 큐레이션" dropdown
 * Displays items in a visually organized grid with column separators
 * Automatically adjusts column count based on number of items:
 * - 1-6 items: 2 columns
 * - 7-12 items: 3 columns
 * - 13+ items: 4 columns
 */
export const DropdownGridLayout = ({
    children,
    currentTag,
    onSettingsClick
}: DropdownGridLayoutProps) => {
    // Dynamically calculate optimal column count based on number of items
    const getColumnCount = (itemCount: number) => {
        if (itemCount <= 6) return 2;
        if (itemCount <= 12) return 3;
        return 4;
    };

    const columnCount = getColumnCount(children.length);
    const itemsPerColumn = Math.ceil(children.length / columnCount);

    // Split children into dynamic columns
    const columns = Array.from({ length: columnCount }, (_, i) => {
        const start = i * itemsPerColumn;
        const end = start + itemsPerColumn;
        return children.slice(start, end);
    });

    return (
        <>
            {columns.map((columnItems, columnIndex) => (
                <div
                    key={`column-${columnIndex}`}
                    className="group/column relative flex flex-1 flex-col gap-2
                        px-5"
                    style={{
                        animation: `slideInStagger 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${columnIndex * 0.08}s backwards`
                    }}
                >
                    {/* Decorative column separator */}
                    {columnIndex < columnCount - 1 && (
                        <div
                            className="absolute -right-3 bottom-0 top-0 w-px
                                opacity-30"
                            style={{
                                background:
                                    "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.4) 20%, rgba(255,255,255,0.4) 80%, transparent 100%)"
                            }}
                        />
                    )}

                    {columnItems.map((child, itemIndex) => {
                        // Check if this child is the currently selected tag
                        const isChildSelected =
                            currentTag &&
                            child.href.includes(`tag=${currentTag}`);

                        return (
                            <DropdownItem
                                key={`${child.label}-${itemIndex}`}
                                child={child}
                                isSelected={!!isChildSelected}
                                animationDelay={getStaggerDelay(
                                    columnIndex,
                                    itemIndex
                                )}
                                onSettingsClick={
                                    (child as any).isSettings
                                        ? onSettingsClick
                                        : undefined
                                }
                            />
                        );
                    })}
                </div>
            ))}
        </>
    );
};
